import { CalendarDate, getLocalTimeZone, parseDate } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';

export type DateRangeValue = RangeValue<DateValue> | null;

type IsoDateRange = {
  start?: string;
  end?: string;
};

/**
 * Converts a DateRangePicker value into ISO strings that cover the entire day range.
 * Start values snap to 00:00:00 and end values snap to 23:59:59.999 in the user's timezone.
 */
export function toInclusiveIsoRange(range: DateRangeValue): IsoDateRange {
  if (!range) {
    return {};
  }

  const { start, end } = range;
  if (!start || !end) {
    return {};
  }

  const timeZone = getLocalTimeZone();
  const startDate = new Date(start.toDate(timeZone));
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(end.toDate(timeZone));
  endDate.setHours(23, 59, 59, 999);

  return {
    start: startDate.toISOString(),
    end: endDate.toISOString(),
  };
}

export function isRangeSelected(range: DateRangeValue): boolean {
  return Boolean(range && range.start && range.end);
}

/**
 * Parses ISO date strings back into a DateRangeValue.
 * For full ISO timestamps (produced by toInclusiveIsoRange), converts to the
 * user's local timezone before extracting date components — otherwise an end
 * timestamp like 23:59:59.999 local-EDT serializes as UTC the next day, and
 * slicing the UTC prefix yields the wrong calendar date.
 */
export function parseIsoToDateRange(
  startIso: string | undefined,
  endIso: string | undefined
): DateRangeValue {
  if (!startIso || !endIso) return null;
  const start = isoToCalendarDate(startIso);
  const end = isoToCalendarDate(endIso);
  return start && end ? { start, end } : null;
}

function isoToCalendarDate(iso: string): CalendarDate | null {
  // Date-only strings (YYYY-MM-DD) have no timezone — parse directly.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    try {
      return parseDate(iso);
    } catch {
      return null;
    }
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

export function formatDateRangeDisplay(range: DateRangeValue): string | null {
  if (!range) {
    return null;
  }

  const { start, end } = range;
  if (!start || !end) {
    return null;
  }

  const timeZone = getLocalTimeZone();
  const startDate = start.toDate(timeZone);
  const endDate = end.toDate(timeZone);
  const sameYear = startDate.getFullYear() === endDate.getFullYear();

  const startFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });

  const endFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${startFormatter.format(startDate)} – ${endFormatter.format(endDate)}`;
}
